<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import AgentActionBaseWidget from './AgentActionBaseWidget.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { IconUser } from '@tabler/icons-vue'
  import { UsersService, type TUser } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectLawyer: 'Select clerk...',
      noLawyersFound: 'No clerks found',
      loadingLawyers: 'Loading clerks...',
      assignText: 'Assign',
      asLawyerText: 'as case handler.',
      headerText: 'Assign Case Handler',
      lawyerAssigned: 'The clerk',
      wasAssignedAsHandler: 'was assigned as case handler.'
    },
    de: {
      selectLawyer: 'Sachbearbeiter auswählen...',
      noLawyersFound: 'Keine Sachbearbeiter gefunden',
      loadingLawyers: 'Lade Sachbearbeiter...',
      assignText: 'Setze',
      asLawyerText: 'als Sachbearbeiter ein.',
      headerText: 'Sachbearbeiter zuweisen',
      lawyerAssigned: 'Der Sachbearbeiter',
      wasAssignedAsHandler: 'wurde als Sachbearbeiter zugewiesen.'
    }
  })

  // Define props
  interface Props {
    action: any // TAgentAction type from generated types
    customPayload?: any // Optional custom payload from parent
  }

  const props = defineProps<Props>()

  // Define emits
  const emit = defineEmits<{
    payloadChanged: [payload: any]
  }>()

  // Reactive state
  const users = ref<TUser[]>([])
  const isLoadingUsers = ref(false)
  const selectedLawyerUuid = ref<string>('')

  // Computed properties
  const lawyerOptions = computed<Option[]>(() => {
    return users.value.map((user) => ({
      value: user.uuid || '',
      title: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      icon: undefined // Could add user avatar or role icon here
    }))
  })

  const selectedLawyer = computed(() => {
    return users.value.find((user) => user.uuid === selectedLawyerUuid.value)
  })

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false && selectedLawyerUuid.value)
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedLawyer.value) return ''

    const lawyerName =
      `${selectedLawyer.value.first_name || ''} ${selectedLawyer.value.last_name || ''}`.trim() ||
      selectedLawyer.value.email

    return `${t('lawyerAssigned')} "${lawyerName}" ${t('wasAssignedAsHandler')}`
  })

  // Methods
  const fetchUsers = async () => {
    isLoadingUsers.value = true
    try {
      const response = await UsersService.usersList({ limit: 100 })
      users.value = response.results || []
    } catch (error) {
      console.error('Error fetching users:', error)
      users.value = []
    } finally {
      isLoadingUsers.value = false
    }
  }

  const handleLawyerSelection = (lawyerUuid: string) => {
    selectedLawyerUuid.value = lawyerUuid
    emitPayloadChange()
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    if (props.action?.payload?.case_uuid) {
      const payload = {
        case_uuid: props.action.payload.case_uuid,
        clerk_uuids: [selectedLawyerUuid.value]
      }
      emit('payloadChanged', payload)
    }
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: any) => {
    if (!payload) return

    if (payload.clerk_uuids?.length) {
      selectedLawyerUuid.value = payload.clerk_uuids[0]
    }
  }

  // Watch for action payload changes
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    },
    { deep: true }
  )

  // Watch for custom payload changes
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    },
    { deep: true }
  )

  // Initialize component
  onMounted(async () => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Fetch available users
    await fetchUsers()

    // Emit initial payload if any values are set
    if (selectedLawyerUuid.value) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('assignText') }}</span>

        <!-- Lawyer Selection -->
        <template v-if="isLoadingUsers">
          <SkeletonLoaderInlineField
            width="200px"
            height="32px"
            class="inline-block"
          />
        </template>
        <template v-else>
          <SelectField
            v-model="selectedLawyerUuid"
            variant="inline-text"
            :options="lawyerOptions"
            :is-filterable="false"
            :disabled="isLoadingUsers"
            :placeholder="lawyerOptions.length === 0 ? t('noLawyersFound') : t('selectLawyer')"
            name="lawyer"
            @update:model-value="handleLawyerSelection"
          />
        </template>

        <span>{{ t('asLawyerText') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
